import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useConnectionState, useJoin, useRemoteUsers } from 'agora-rtc-react';

import { AppContext, usePrepareAppState } from '@/app/AppContext';
import { APP_ID } from '@/app/config';
import { useAutoRecording, useRecording } from '@/app/mutations/RecordingMutation/context';
import { useAutoTranscription, useTranscription } from '@/app/mutations/TranscriptionMutation/context';
import useLocationBreadcrumbs from '@/app/location/useLocationBreadcrumbs';
import { useJoinData, useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';
import useAbandonedSession, {
  ABANDONED_AFTER_MS,
  HOST_ABANDONED_AFTER_MS,
} from '@/app/hooks/useAbandonedSession';
import useSoundEffects from '@/app/hooks/useSoundEffects';
import MeetingView from './MeetingView';
import { Permissions } from '../../types';


interface IMeetingPageProps {
  onEndCall(): void;
  // Separate from onEndCall so the end-call screen can tell a call the user
  // finished from one that ran out of people and closed itself.
  onAbandoned(): void;
  permissions: Permissions
}

export default function MeetingPage({ onAbandoned, onEndCall, permissions }: IMeetingPageProps) {
  const userData = useUserData();
  const appState = usePrepareAppState(permissions, userData.isHost);
  const { playJoinSound } = useSoundEffects();

  const joinData = useJoinData(APP_ID);

  useJoin(joinData, true);

  const recording = useRecording();
  const transcription = useTranscription();

  // Real RTC connection state drives the presence gate, read as "has not left"
  // rather than "is perfectly connected right now". A host RECONNECTING is a
  // network wobble, and at a 60s grace `useIsConnected()` (strictly CONNECTED)
  // would tear the recording down over one — losing real footage. Only a real
  // DISCONNECTED stops the feature. A one-sided start is still impossible: no
  // remote user is present until this client has actually connected.
  const joined = useConnectionState() !== 'DISCONNECTED';
  const remoteUsers = useRemoteUsers();
  const { participants } = useMeetingData();

  // Once the user deliberately ends the call we disable the gate so it cannot
  // restart while the feedback dialog is still open (both sides are still in the
  // channel at that point). The effect below sends the actual prompt stop.
  const [callEnding, setCallEnding] = useState(false);
  const recordingEnabled = userData.isHost && import.meta.env.VITE_ENABLE_RECORDING;

  useAutoRecording({ joined }, recordingEnabled && !callEnding);
  useAutoTranscription({ joined }, recordingEnabled && !callEnding);
  useLocationBreadcrumbs(userData.isClient);

  const handleBeforeEndCall = useCallback(() => {
    // Deliberate meeting end: disable the gate so it can't (re)start. The effect
    // below drives the actual prompt stop.
    setCallEnding(true);
  }, []);

  // A tab left open holds the channel open, and Agora counts it as a present
  // participant — which is exactly why the server's presence-based stop cannot
  // see this case. Leaving takes the same route as a deliberate end: stop the
  // recording first, then unmount, which drops presence and releases the
  // channel. See CAD-6901, and CAD-6870 for what these sessions cost.
  //
  // Navigating away is deferred to the effect below rather than done here.
  // Calling onEndCall() alongside setCallEnding(true) batches both updates, and
  // the parent swaps the page out in the same commit — so the stop effect never
  // gets a render to run in and the recording is left running, which is the one
  // thing this is for. The deliberate path survives that only because the
  // feedback dialog keeps the page mounted in between.
  const [isAbandoned, setIsAbandoned] = useState(false);

  const handleAbandonedSession = useCallback(() => {
    handleBeforeEndCall();
    setIsAbandoned(true);
  }, [handleBeforeEndCall]);

  // Every remote UID is not a participant: a screen share joins as one of its
  // own, so counting them would leave a host who wandered off mid-share looking
  // present forever. Presence means somebody else's camera, as the recording
  // gate already works out in useAutoCallFeature.
  const participantCameraUids = useMemo(
    () => new Set(participants.map(p => p.user_id)),
    [participants]
  );
  const isAnyoneElsePresent = useMemo(
    () => remoteUsers.some(user => participantCameraUids.has(user.uid as number)),
    [participantCameraUids, remoteUsers]
  );

  // Without a roster there is no UID to recognise anybody by, so everyone in the
  // channel would read as a stranger and a live call would be hung up on. The
  // meeting query gates rendering on its data, so this should not happen —
  // which is exactly why it should not be the thing that ends a call.
  const knowsWhoIsExpected = participants.length > 0;

  // Only a call that happened can be abandoned. A room nobody ever joined is not
  // being recorded — the gate needs both sides present before it starts — so it
  // costs nothing to leave sitting there, and closing it would mean hanging up on
  // an account manager waiting for a late customer, which is the one thing this
  // must not do. The expensive case (CAD-6870) is the other one: the call ran,
  // everybody left, and a tab stayed behind holding the channel open.
  const [hasCallHappened, setHasCallHappened] = useState(false);
  useEffect(function rememberThatSomebodyCame() {
    if (isAnyoneElsePresent) {
      setHasCallHappened(true);
    }
  }, [isAnyoneElsePresent]);

  useAbandonedSession(
    joined && knowsWhoIsExpected && hasCallHappened && !isAnyoneElsePresent,
    handleAbandonedSession,
    userData.isHost ? HOST_ABANDONED_AFTER_MS : ABANDONED_AFTER_MS,
  );

  // On deliberate call end, force a prompt, non-rate-limited stop as soon as the
  // feature is active. If a start is still in flight when the host ends the call,
  // this fires the moment that start resolves (the active flag flips true) — so a
  // deliberate end never falls back to the server backstop, and it never sends a
  // spurious stop when nothing ever started (e.g. a customer no-show). The ref
  // makes each stop one-shot: the two features stop independently, so without it
  // one flag flipping would re-run this effect and re-send the other's stop.
  const endStopSentRef = useRef({ recording: false, transcription: false });
  useEffect(() => {
    if (!callEnding) return;
    if (recording.isRecording && !endStopSentRef.current.recording) {
      endStopSentRef.current.recording = true;
      recording.stop({ force: true });
    }
    if (transcription.isTranscribing && !endStopSentRef.current.transcription) {
      endStopSentRef.current.transcription = true;
      transcription.stop({ force: true });
    }
  }, [callEnding, recording, transcription]);

  // Declared after the stop effect so it runs after it: within one commit React
  // runs effects in order, so the stops are dispatched before this hands the
  // page over and unmounts everything. Their requests are already in flight by
  // then and outlive the component.
  useEffect(function leaveOnceTeardownHasBeenDispatched() {
    if (!isAbandoned) return;
    onAbandoned();
  }, [isAbandoned, onAbandoned]);

  useEffect(() => {
    playJoinSound();
  }, [playJoinSound]);

  return (
    <AppContext.Provider value={appState}>
      <MeetingView onEndCall={onEndCall} onBeforeEndCall={handleBeforeEndCall} />
    </AppContext.Provider>
  );
}
